/**
 * PasswordDialog - Password entry for encrypted databases
 *
 * Two modes:
 * - "create": Set new password with confirmation for encrypting a new database
 * - "unlock": Enter password to unlock an existing encrypted database
 */

import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, Eye, EyeOff, AlertCircle, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================================
// Types
// ============================================================================

export interface PasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "unlock";
  onSubmit: (password: string) => void;
  error?: string | null;
  fileName?: string;
  loading?: boolean;
  failedAttempts?: number;
}

// ============================================================================
// Password Strength
// ============================================================================

type PasswordStrength = "weak" | "fair" | "good" | "strong";

function checkPasswordStrength(password: string): PasswordStrength {
  if (!password || password.length < 8) return "weak";

  let score = 0;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score <= 1) return "weak";
  if (score <= 2) return "fair";
  if (score <= 3) return "good";
  return "strong";
}

const strengthConfig: Record<PasswordStrength, { label: string; color: string; width: string }> = {
  weak: { label: "Weak", color: "bg-red-500", width: "w-1/4" },
  fair: { label: "Fair", color: "bg-orange-500", width: "w-2/4" },
  good: { label: "Good", color: "bg-yellow-500", width: "w-3/4" },
  strong: { label: "Strong", color: "bg-green-500", width: "w-full" },
};

// ============================================================================
// Schemas
// ============================================================================

const createPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(128, "Password is too long"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

const unlockPasswordSchema = z.object({
  password: z.string().min(1, "Password is required"),
});

type CreatePasswordFormData = z.infer<typeof createPasswordSchema>;
type UnlockPasswordFormData = z.infer<typeof unlockPasswordSchema>;

// ============================================================================
// Component
// ============================================================================

export function PasswordDialog({
  open,
  onOpenChange,
  mode,
  onSubmit,
  error,
  fileName,
  loading = false,
  failedAttempts = 0,
}: PasswordDialogProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);

  // Progressive delay: 0, 0, 0, 2s, 4s, 8s, 16s... after 3 failed attempts
  useEffect(() => {
    if (mode === "unlock" && failedAttempts >= 3) {
      const delay = Math.pow(2, failedAttempts - 3);
      setLockoutSeconds(delay);
      const interval = setInterval(() => {
        setLockoutSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [mode, failedAttempts]);

  // Use different schemas based on mode
  const schema = mode === "create" ? createPasswordSchema : unlockPasswordSchema;

  const form = useForm<CreatePasswordFormData | UnlockPasswordFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: "",
      ...(mode === "create" ? { confirmPassword: "" } : {}),
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        password: "",
        ...(mode === "create" ? { confirmPassword: "" } : {}),
      });
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [open, mode, form]);

  const handleSubmit = useCallback(
    (data: CreatePasswordFormData | UnlockPasswordFormData) => {
      onSubmit(data.password);
    },
    [onSubmit]
  );

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  const isCreate = mode === "create";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            {isCreate ? "Set Database Password" : "Unlock Database"}
          </DialogTitle>
          <DialogDescription>
            {isCreate ? (
              "Create a password to encrypt your database. This password will be required to open the database in the future."
            ) : (
              <>
                Enter the password to unlock{" "}
                {fileName && (
                  <span className="font-medium">{fileName}</span>
                )}
                .
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder={
                          isCreate
                            ? "Enter a strong password"
                            : "Enter password"
                        }
                        autoComplete={isCreate ? "new-password" : "current-password"}
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={togglePasswordVisibility}
                        tabIndex={-1}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                  {isCreate && field.value && (
                    <div className="mt-2 space-y-1">
                      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                        <div
                          className={cn(
                            "h-full transition-all duration-300",
                            strengthConfig[checkPasswordStrength(field.value)].color,
                            strengthConfig[checkPasswordStrength(field.value)].width
                          )}
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          Strength: {strengthConfig[checkPasswordStrength(field.value)].label}
                        </span>
                        {checkPasswordStrength(field.value) === "weak" && (
                          <span className="flex items-center gap-1 text-orange-600">
                            <AlertTriangle className="h-3 w-3" />
                            Easy to guess
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </FormItem>
              )}
            />

            {isCreate && (
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm your password"
                          autoComplete="new-password"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={toggleConfirmPasswordVisibility}
                          tabIndex={-1}
                          aria-label={
                            showConfirmPassword
                              ? "Hide confirm password"
                              : "Show confirm password"
                          }
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {isCreate && (
              <p className="text-sm text-muted-foreground">
                <strong>Important:</strong> If you forget this password, your
                data cannot be recovered. Store it securely.
              </p>
            )}

            {lockoutSeconds > 0 && (
              <p className="text-sm text-orange-600 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Too many attempts. Try again in {lockoutSeconds}s
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading || lockoutSeconds > 0}>
                {loading
                  ? isCreate
                    ? "Encrypting..."
                    : "Unlocking..."
                  : lockoutSeconds > 0
                  ? `Wait ${lockoutSeconds}s...`
                  : isCreate
                  ? "Create Encrypted Database"
                  : "Unlock"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
